import { ___p, _js } from '@ifixit/utils/localize';
import {
   AlertDialog,
   AlertDialogAction,
   AlertDialogCancel,
   AlertDialogContent,
   AlertDialogDescription,
   AlertDialogFooter,
   AlertDialogHeader,
   AlertDialogTitle,
} from '@ifixit/ui/components/alert-dialog';
import { Badge } from '@ifixit/ui/components/badge';
import { FaIcon } from '@ifixit/ui/components/icon';
import {
   Dialog,
   DialogContent,
   DialogDescription,
   DialogTitle,
} from '@ifixit/ui/components/dialog';
import {
   Pagination,
   PaginationContent,
   PaginationItem,
   PaginationLink,
   PaginationNext,
   PaginationPrevious,
   calculatePageRange,
} from '@ifixit/ui/components/pagination';
import {
   Select,
   SelectContent,
   SelectGroup,
   SelectItem,
   SelectLabel,
   SelectSeparator,
   SelectTrigger,
   SelectValue,
} from '@ifixit/ui/components/select';
import { Skeleton } from '@ifixit/ui/components/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@ifixit/ui/components/tooltip';
import { toast, Toaster } from '@ifixit/ui/components/toast';
import { cn } from '@ifixit/ui/lib/utils';
import {
   type MouseEvent as ReactMouseEvent,
   type ReactNode,
   useEffect,
   useId,
   useRef,
   useState,
} from 'react';
import { EditorFooter } from 'Shared/editor/editor_footer';
import { MessageCard } from 'Shared/editor/message_card';
import { isShortcutClaimed } from 'Shared/editor/shortcut_scope';
import { ShortcutList } from 'Guide/Edit/v2/KeyboardShortcutsDialog';
import type {
   ModerationContent,
   ModerationEntry,
   ModerationFilter,
   ModerationLangFilter,
   ModerationPageData,
   ModerationProps,
   ModerationQueueKey,
   ModerationTopVoter,
   ModerationView,
   ModerationVoter,
} from './moderation_types';
import {
   moderationPageKey,
   useModerationPage,
   useVoteActions,
   type VoteOutcome,
} from './use_moderation_api';

/** The two directions a moderator vote can go, as ModeratorVoteLib scores
 *  them. */
const UP_VOTE = 1;
const DOWN_VOTE = -1;

/** The langFilter value that turns the language filter off, and the URL
 *  param it rides in. Patrol uses the same param name with different
 *  values (its fixed three, not a langid); only 'all' means the same
 *  thing on both pages. */
const ALL_LANGUAGES = 'all';
const LANGUAGE_FILTER_PARAM = 'langFilter';

/**
 * The /Moderation queue: posts and comments the community flagged, each
 * waiting for enough votes to carry out what was proposed or to turn it
 * down. The shell, card and list grammar mirror /Translate and
 * /Leaderboard — sibling tabs in the community nav — so the pages read as
 * one surface.
 */

/** One vote, one card, in the editors' SectionCard weight: gray-300
 *  border, full-bleed with square corners below md. The queue is a stack
 *  of these rather than rows inside one container, because each card is a
 *  separate decision and the gap is what says so. */
const CARD_CLASS =
   'overflow-hidden rounded-2xl border border-gray-300 bg-white max-md:-mx-4 max-md:rounded-none max-md:border-x-0';

export function ModerationDashboard(props: ModerationProps) {
   const [view, setView] = useQueueView(props);
   const isArrivingView =
      view.queue === props.activeQueue &&
      view.page === props.initialData.page &&
      view.filter === props.initialData.filter &&
      view.langFilter === props.initialData.langFilter &&
      view.voteid === props.voteid;
   const query = useModerationPage(view, isArrivingView ? props.initialData : undefined);
   const singleVote = view.voteid !== null;
   const pageData = query.data;
   const [superPowers, setSuperPowers] = useState(false);
   const { castVote, cancelVote } = useVoteActions(moderationPageKey(view));
   // The optimistic patch answers the click; this closes the moment before
   // it lands, when a second click or keypress would submit twice.
   const busyVoteid =
      (castVote.isPending ? castVote.variables?.voteid : undefined) ??
      (cancelVote.isPending ? cancelVote.variables?.voteid : undefined) ??
      null;

   // A vote that settles one takes its card out of the queue, so the toast
   // is the only thing left saying what the click did. It reads the answer
   // rather than the button: pressing the pass side can still end in a
   // refusal, once the tally is counted.
   const report = (entry: ModerationEntry) => ({
      onSuccess: (result: VoteOutcome) => {
         switch (result.outcome) {
            case 'closed': {
               toast.warning(_js('Somebody decided this one first.'));

               break;
            }
            case 'passed': {
               toast.success(_js('Done: %1', entry.actionText));

               break;
            }
            case 'failed': {
               toast.success(_js('Turned down: %1', entry.actionText));

               break;
            }
            // No default
         }
      },
      onError: (error: unknown) => toast.error(voteErrorMessage(error)),
   });

   const vote = (entry: ModerationEntry, score: number) => {
      castVote.mutate({ voteid: entry.voteid, score, adminForce: superPowers }, report(entry));
      // Working the queue is one decision after another, and the card just
      // decided closes under the pointer. The next one that still wants
      // this reader takes the ring, which scrolls it into view and puts P
      // and F on it, so a pass down the page never needs the mouse.
      const next = nextToVote(pageData?.entries ?? [], entry.voteid);
      if (next) {
         focusOn(next.voteid);
      }
   };

   const undoVote = (entry: ModerationEntry) => {
      if (entry.voteid === busyVoteid) {
         return;
      }

      // Withdrawing can only end in 'canceled' or 'closed', so the settled
      // wording never reaches the screen from here.
      cancelVote.mutate({ voteid: entry.voteid }, report(entry));
   };

   // The dialog is held here rather than per card so the P key reaches the
   // same confirmation the button does.
   const [confirming, setConfirming] = useState<ModerationEntry | null>(null);
   const requestVote = (entry: ModerationEntry, score: number) => {
      if (entry.voteid === busyVoteid) {
         return;
      }

      // Super powers decide the vote outright, so an armed pass click
      // confirms even where the action itself could be undone by hand.
      if (
         score === UP_VOTE &&
         carriesOut(entry, superPowers) &&
         (entry.destructive || superPowers)
      ) {
         setConfirming(entry);
         return;
      }

      vote(entry, score);
   };

   const [helpOpen, setHelpOpen] = useState(false);
   // Only the pending queue has anything to vote on, and only a member with
   // the reputation for it: everyone else gets no keys and no offer of them.
   const shortcutsEnabled = view.queue === 'pending' && (pageData?.canVote ?? false);
   const { focused, focusOn } = useQueueShortcuts({
      entries: pageData?.entries ?? [],
      enabled: shortcutsEnabled,
      onVote: requestVote,
      onHelp: () => setHelpOpen(true),
   });

   const goTo = (next: Partial<ModerationView>) => {
      // Any navigation leaves the permalink: the queues are what the
      // tabs, filter and pager move between.
      setView({ ...view, voteid: null, ...next });
      window.scrollTo({ top: 0, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
   };

   return (
      <div className="mx-auto w-full max-w-5xl px-4 pb-10 md:px-8">
         <header className="pt-6">
            {/* The language filter sits beside the H1, above the
                description, where the redesigned Patrol put the same
                control: the two review surfaces read as one system. */}
            <div className="flex flex-wrap items-center justify-between gap-4">
               <h1 className="text-[30px] font-medium text-gray-950">{_js('Moderation')}</h1>
               <LanguageFilter
                  langFilter={view.langFilter}
                  viewLanguage={props.viewLanguage}
                  otherLanguages={props.otherLanguages}
                  onSelect={langFilter => goTo({ langFilter, page: 1 })}
               />
            </div>
            <p className="mt-2 max-w-2xl text-sm text-gray-700">
               {_js(
                  'Help make iFixit awesome by removing off-topic or unhelpful posts and comments. Anyone with at least %1 reputation points can vote.',
                  String(props.initialData.reputationThreshold)
               )}{' '}
               <a
                  href={props.helpUrl}
                  className="whitespace-nowrap font-medium text-brand-500 underline-offset-2 hover:text-brand-600 hover:underline"
               >
                  {_js('How moderation works')}
               </a>
            </p>
         </header>

         <TopVoters voters={props.topVoters} leaderboardUrl={props.leaderboardUrl} />

         <div className="mt-6 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
            <QueueTabs
               queues={props.queues}
               activeQueue={singleVote ? null : view.queue}
               viewUrl={queue =>
                  viewUrl(props, {
                     queue,
                     page: 1,
                     filter: 'all',
                     langFilter: view.langFilter,
                     voteid: null,
                  })
               }
               onSelect={singleVote ? undefined : queue => goTo({ queue, page: 1, filter: 'all' })}
            />
            <div className="flex flex-wrap items-center gap-3">
               {pageData?.canForceVote && (
                  <SuperPowers enabled={superPowers} onChange={setSuperPowers} />
               )}
               {props.isLoggedIn && view.queue === 'pending' && (
                  <VotedFilter
                     filter={view.filter}
                     onSelect={filter => goTo({ filter, page: 1 })}
                  />
               )}
            </div>
         </div>

         {singleVote && (
            <p className="mt-4 text-sm text-gray-700">
               {_js('You are looking at one flagged post.')}{' '}
               <a
                  href={props.queues[0]?.url ?? '/Moderation'}
                  className="font-medium text-brand-500 underline-offset-2 hover:text-brand-600 hover:underline"
               >
                  {_js('Back to the queue')}
               </a>
            </p>
         )}

         <section className="mt-4">
            <QueueBody
               query={query}
               pageData={pageData}
               voting={{
                  isLoggedIn: props.isLoggedIn,
                  loginUrl: props.loginUrl,
                  reputationThreshold: props.initialData.reputationThreshold,
                  viewerReputation: pageData?.viewerReputation ?? null,
               }}
               focused={focused}
               busyVoteid={busyVoteid}
               onVote={requestVote}
               onUndo={undoVote}
            />
         </section>
         {/* One short line, rather than a live region around the list:
             announcing the region would read all ten flagged posts again
             every time the page or the queue changed. */}
         <p aria-live="polite" className="sr-only">
            {pageData
               ? ___p(
                    pageData.entries.length,
                    '%1 flagged post, page %2',
                    '%1 flagged posts, page %2',
                    String(pageData.entries.length),
                    String(view.page)
                 )
               : _js('Loading')}
         </p>

         {pageData && (
            <QueuePagination
               view={view}
               pageData={pageData}
               pageUrl={page => viewUrl(props, { ...view, page })}
               onPageChange={page => goTo({ page })}
            />
         )}

         {shortcutsEnabled && (
            <EditorFooter>
               <button
                  type="button"
                  onClick={() => setHelpOpen(true)}
                  aria-label={_js('Keyboard shortcuts')}
                  // Physical keyboards are a desktop affordance; hide the
                  // shortcuts entry on phones and tablets, where it's noise.
                  className="hidden rounded-sm text-gray-400 hover:text-gray-600 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 lg:block"
               >
                  <FaIcon name="keyboard" className="size-4" />
               </button>
            </EditorFooter>
         )}

         <ConfirmDecision
            entry={confirming}
            superPowers={superPowers}
            onOpenChange={open => !open && setConfirming(null)}
            onConfirm={entry => {
               setConfirming(null);
               vote(entry, UP_VOTE);
            }}
         />
         <ShortcutsDialog open={helpOpen} onOpenChange={setHelpOpen} />
         <Toaster position="top-center" />
      </div>
   );
}

/**
 * The next card in the queue that still wants this reader's vote, starting
 * below the one they just decided and wrapping to the top, since the ones
 * above may have been skipped rather than voted on.
 */
function nextToVote(entries: ModerationEntry[], from: number): ModerationEntry | null {
   const at = entries.findIndex(entry => entry.voteid === from);
   const wants = (entry: ModerationEntry) => entry.canVote && entry.myVote === null;

   for (let step = 1; step <= entries.length; step++) {
      const entry = entries[(at + step) % entries.length];
      if (entry && entry.voteid !== from && wants(entry)) {
         return entry;
      }
   }

   return null;
}

/** Whether this click does the thing rather than counting toward it. */
function carriesOut(entry: ModerationEntry, superPowers: boolean): boolean {
   return superPowers || entry.score + UP_VOTE >= entry.passThreshold;
}

/** What every card needs to say where the voting is and why it is or
 *  isn't open to the reader. */
interface VotingContext {
   isLoggedIn: boolean;
   loginUrl: string;
   reputationThreshold: number;
   viewerReputation: number | null;
}

function QueueBody({
   query,
   pageData,
   voting,
   focused,
   busyVoteid,
   onVote,
   onUndo,
}: {
   query: ReturnType<typeof useModerationPage>;
   pageData: ModerationPageData | undefined;
   voting: VotingContext;
   focused: number;
   busyVoteid: number | null;
   onVote: (entry: ModerationEntry, score: number) => void;
   onUndo: (entry: ModerationEntry) => void;
}) {
   if (query.isError) {
      return (
         <div className={CARD_CLASS}>
            <MessageCard
               icon="triangle-exclamation"
               tone="failure"
               title={_js("This queue didn't load")}
               text={_js('Reload the page if this keeps happening.')}
               action={
                  <button
                     type="button"
                     className="button-md button-outline max-md:min-h-11"
                     disabled={query.isFetching}
                     onClick={() => void query.refetch()}
                  >
                     {query.isFetching ? _js('Trying again...') : _js('Try again')}
                  </button>
               }
            />
         </div>
      );
   }

   // keepPreviousData holds the queue you came from on screen while the
   // next one loads, which reads as nothing having happened when you click
   // a tab. The cards you are looking at are the wrong queue's, so they
   // give way to their own shape until the right ones arrive.
   if (!pageData || query.isPlaceholderData) {
      return <QueueSkeleton rows={pageData ? pageData.entries.length : 3} />;
   }

   if (pageData.entries.length === 0) {
      return (
         <div className={CARD_CLASS}>
            <MessageCard
               icon="thumbs-up"
               tone="done"
               title={_js('Nothing waiting')}
               text={_js('Every flagged post in this queue has been decided.')}
            />
         </div>
      );
   }

   return (
      <ul aria-label={_js('Flagged posts')} className="m-0 grid list-none gap-3 p-0">
         {pageData.entries.map((entry, index) => (
            <VoteCard
               key={entry.voteid}
               entry={entry}
               viewerid={pageData.viewerid}
               voting={voting}
               focused={index === focused}
               busy={entry.voteid === busyVoteid}
               onVote={onVote}
               onUndo={onUndo}
            />
         ))}
      </ul>
   );
}

function VoteCard({
   entry,
   viewerid,
   voting,
   focused,
   busy,
   onVote,
   onUndo,
}: {
   entry: ModerationEntry;
   viewerid: number | null;
   voting: VotingContext;
   focused: boolean;
   busy: boolean;
   onVote: (entry: ModerationEntry, score: number) => void;
   onUndo: (entry: ModerationEntry) => void;
}) {
   const card = useRef<HTMLLIElement | null>(null);
   // A card you have voted on closes: the queue is worked ten at a time,
   // and one that still needs you should not have to be told apart from
   // one that is done. Opening it again is a click, and the state is the
   // vote rather than the click, so a reload closes it again.
   const [open, setOpen] = useState(false);
   const settled = entry.status === 'pending' && entry.myVote !== null && !open;

   // Walking the queue from the keyboard has to bring the card with it,
   // 'nearest' so a card already in view doesn't jump.
   useEffect(() => {
      if (focused) {
         card.current?.scrollIntoView?.({ block: 'nearest' });
      }
   }, [focused]);

   if (settled) {
      return (
         <li
            ref={card}
            className={cn(
               CARD_CLASS,
               'p-4',
               entry.myVote !== null && entry.myVote > 0
                  ? 'border-green-200 bg-green-50'
                  : 'border-red-200 bg-red-50',
               focused && 'ring-2 ring-inset ring-brand-300'
            )}
         >
            <div className="flex items-center justify-between gap-4">
               {/* The mark sits beside the heading rather than inside it:
                   heading navigation should hear the action, not be led
                   into it by how the reader voted. */}
               <div className="flex min-w-0 items-center gap-2">
                  <VoteMark score={entry.myVote ?? 0} />
                  <h2 className="m-0 min-w-0 truncate text-base font-semibold text-gray-950">
                     {entry.actionText}
                  </h2>
               </div>
               <VoteTally entry={entry} viewerid={viewerid} />
            </div>
            <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-2">
               <span className="min-w-0 flex-1 truncate text-xs text-gray-600">
                  {reference(entry)}
               </span>
               <div className="flex shrink-0 gap-2 max-md:w-full">
                  <button
                     type="button"
                     className="button-sm button-outline max-md:min-h-11 max-md:flex-1"
                     onClick={() => setOpen(true)}
                  >
                     {_js('Show details')}
                  </button>
                  <button
                     type="button"
                     className="button-sm button-outline max-md:min-h-11 max-md:flex-1"
                     disabled={busy}
                     onClick={() => onUndo(entry)}
                  >
                     {_js('Undo my vote')}
                  </button>
               </div>
            </div>
         </li>
      );
   }

   return (
      <li
         ref={card}
         className={cn(CARD_CLASS, 'px-4 pt-4', focused && 'ring-2 ring-inset ring-brand-300')}
      >
         <div className="flex items-center justify-between gap-4">
            <h2 className="m-0 min-w-0 text-base font-semibold text-gray-950">
               {entry.actionText}
               {/* Ten cards can share one action, so heading navigation
                   hears ten identical headings without this. */}
               <span className="sr-only">
                  {entry.subject.author
                     ? _js(
                          'on %1 by %2',
                          entry.subject.title ?? entry.subject.kind,
                          entry.subject.author.name
                       )
                     : _js('on %1', entry.subject.title ?? entry.subject.kind)}
               </span>
            </h2>
            <VoteTally entry={entry} viewerid={viewerid} />
         </div>

         <div className="mt-3">
            {entry.context && (
               <>
                  <ContentCard content={entry.context} url={entry.url} quiet />
                  <div className="ml-7 h-3 w-0.5 bg-gray-300" aria-hidden="true" />
               </>
            )}
            <ContentCard content={entry.subject} url={entry.url} />
         </div>

         {/* The strips run to the card's edges and carry their own
             padding, so whichever ends the card closes it evenly. */}
         <footer className="-mx-4 mt-3 flex flex-wrap items-center gap-2 border-t border-gray-200 px-4 py-3 text-xs text-gray-600">
            <span>{_js('Flagged %1', entry.flaggedLabel)}</span>
            <span className="text-gray-400">·</span>
            <Voters entry={entry} viewerid={viewerid} />
         </footer>
         <VoteBar
            entry={entry}
            viewerid={viewerid}
            voting={voting}
            busy={busy}
            onCollapse={entry.myVote === null ? undefined : () => setOpen(false)}
            onVote={onVote}
            onUndo={onUndo}
         />
      </li>
   );
}

/**
 * Who wrote it, then what they wrote. The question that gives context and
 * the post being voted on are the same object twice, so they take the same
 * header; only the surface tells them apart. The title leads the body
 * rather than the header because answers and comments never have one.
 *
 * The question keeps its words behind a control. Two blocks of prose, one
 * above the other and built the same way, left the reader working out
 * which of them the vote was about, and the title is what says whether a
 * flag makes sense: of the flagged posts waiting today, every one whose
 * question is on the card has a title, and the words under it run to two
 * lines. One click has them back.
 */
function ContentCard({
   content,
   url,
   quiet = false,
}: {
   content: ModerationContent;
   url: string;
   quiet?: boolean;
}) {
   return (
      <article
         className={cn(
            'rounded-xl border',
            quiet ? 'border-gray-200 bg-gray-50' : 'border-gray-300 bg-white'
         )}
      >
         <header className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-gray-200 px-3 py-2 text-xs text-gray-600">
            <Badge className={cn('badge-md', quiet ? 'badge-gray' : 'badge-brand')}>
               {content.kind}
            </Badge>
            {content.author && (
               <>
                  <a
                     href={content.author.url}
                     className="flex items-center gap-1.5 font-semibold text-gray-900 no-underline hover:text-brand-500"
                  >
                     <img
                        src={content.author.avatar}
                        alt=""
                        className="size-5 shrink-0 rounded-full object-cover"
                        loading="lazy"
                     />
                     {content.author.name}
                  </a>
                  <span className="text-gray-400">·</span>
                  <span>{_js('%1 rep', String(content.author.reputation))}</span>
               </>
            )}
            {content.dateLabel && (
               <>
                  <span className="text-gray-400">·</span>
                  <span>{content.dateLabel}</span>
               </>
            )}
            <a href={url} className="ml-auto text-brand-500 no-underline hover:underline">
               {_js('Open in Answers')}
            </a>
         </header>
         <div className="px-3 py-3">
            {content.title && (
               <a
                  href={url}
                  className="mb-1 block font-semibold text-gray-950 no-underline hover:text-brand-500"
               >
                  {content.title}
               </a>
            )}
            {/* The server hands over transformed markup, the same it feeds
                the post page. */}
            {quiet && content.title ? (
               <ClosedProse html={content.text} className={PROSE_QUIET} />
            ) : (
               <FoldedProse
                  html={content.text}
                  // The question is context; the flagged post is the decision.
                  fold={quiet ? 'max-h-32' : 'max-h-64'}
                  className={cn('prose prose-default', PROSE_HEADINGS, quiet && PROSE_QUIET_TEXT)}
               />
            )}
         </div>
      </article>
   );
}

/** The prose a post carries can hold headings of its own — "Update
 *  (06/29/26)" and the like — and prose sizes those for a page, not for a
 *  card in a queue, where they end up louder than the card's own title. */
const PROSE_HEADINGS =
   '[&_:is(h1,h2,h3,h4,h5,h6)]:my-2 [&_:is(h1,h2,h3,h4,h5,h6)]:text-sm [&_:is(h1,h2,h3,h4,h5,h6)]:font-semibold';

const PROSE_QUIET_TEXT = 'text-[13px] text-gray-600';
const PROSE_QUIET = cn('prose prose-default', PROSE_HEADINGS, PROSE_QUIET_TEXT);

const DISCLOSURE_CLASS =
   'mt-2 rounded-sm text-xs font-medium text-brand-500 hover:text-brand-600 hover:underline focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50';

/** Put away whatever its length, unlike the fold below, which only takes a
 *  control once there is something to unfold. */
function ClosedProse({ html, className }: { html: string; className?: string }) {
   const [open, setOpen] = useState(false);
   const bodyId = useId();

   if (html === '') {
      return null;
   }

   return (
      <>
         <div
            id={bodyId}
            hidden={!open}
            className={className}
            dangerouslySetInnerHTML={{ __html: html }}
         />
         <button
            type="button"
            aria-expanded={open}
            aria-controls={bodyId}
            onClick={() => setOpen(!open)}
            className={DISCLOSURE_CLASS}
         >
            {open ? _js('Show less') : _js('Show more')}
         </button>
      </>
   );
}

/** Which way the reader went, in the heading of a card they have closed. */
function VoteMark({ score }: { score: number }) {
   return (
      <span
         className={cn(
            'flex size-5 shrink-0 items-center justify-center rounded-full text-white',
            score > 0 ? 'bg-green-600' : 'bg-red-600'
         )}
      >
         <FaIcon name={score > 0 ? 'check' : 'xmark'} className="size-2.5" />
         <span className="sr-only">
            {score > 0 ? _js('You voted in favour') : _js('You voted against')}
         </span>
      </span>
   );
}

/**
 * What a closed card was about, in one line: enough to know which post it
 * was without opening it again. The question's title names the thread the
 * flagged post sits in, which is what the card shows when it is open.
 */
function reference(entry: ModerationEntry): string {
   const kind = entry.subject.kind;
   const author = entry.subject.author?.name;
   const title = entry.context?.title ?? entry.subject.title;

   if (author && title) {
      return _js('%1 by %2 on %3', kind, author, title);
   }
   if (author) {
      return _js('%1 by %2', kind, author);
   }

   return title ? _js('%1 on %2', kind, title) : kind;
}

/**
 * Only what overruns the fold gets a control, so the short posts, which
 * are most of them, read exactly as they did.
 */
function FoldedProse({
   html,
   fold,
   className,
}: {
   html: string;
   fold: string;
   className?: string;
}) {
   const body = useRef<HTMLDivElement | null>(null);
   const [overruns, setOverruns] = useState(false);
   const [open, setOpen] = useState(false);
   const bodyId = useId();

   useEffect(() => {
      const element = body.current;
      // Unfolded, scrollHeight and clientHeight agree whatever the length,
      // so re-measuring then would retract the control that got us here.
      if (element && !open) {
         setOverruns(element.scrollHeight > element.clientHeight + 1);
      }
   }, [html, open]);

   return (
      <>
         <div
            id={bodyId}
            ref={body}
            className={cn(className, !open && `${fold} overflow-hidden`)}
            dangerouslySetInnerHTML={{ __html: html }}
         />
         {overruns && (
            <button
               type="button"
               aria-expanded={open}
               aria-controls={bodyId}
               onClick={() => setOpen(!open)}
               className={DISCLOSURE_CLASS}
            >
               {open ? _js('Show less') : _js('Show more')}
            </button>
         )}
      </>
   );
}

/**
 * A tally with two ends: it passes at the action's threshold and fails at
 * -2, so a bar that only fills toward passing would read a vote that is
 * being turned down as one that has stalled.
 *
 * Only while it is pending, though. ModeratorVoteLib sums pending rows
 * alone, so a settled vote scores zero however it went, and a meter here
 * would announce "no votes yet" over the list of people who cast them.
 * Those cards say how it ended instead.
 */
function VoteTally({ entry, viewerid }: { entry: ModerationEntry; viewerid: number | null }) {
   if (entry.status !== 'pending') {
      return <VoteOutcomeBadge entry={entry} />;
   }

   // The pips fill with the net, which is what the vote is decided on:
   // one against cancels one in favour, and the ends of the row are the
   // two thresholds. Counting each side instead put a lit pip next to an
   // empty one and promised a removal that was two votes away.
   //
   // The reader's own vote is marked on the pip their side has reached.
   // Where their side has reached nothing, because the other side has
   // cancelled them out, the mark is an empty ring on the first pip they
   // would light: their vote is on the board and it has moved nothing.
   const { inFavour, against } = sides(entry, viewerid);
   const mine = entry.myVote ?? 0;
   const ringAt =
      mine === 0
         ? null
         : Math.sign(mine) === Math.sign(entry.score)
           ? entry.score
           : Math.sign(mine);

   const failPips = [];
   for (let n = entry.failThreshold; n < 0; n++) {
      failPips.push(<Pip key={n} on={entry.score <= n} tone="fail" mine={n === ringAt} />);
   }
   const passPips = [];
   for (let n = 1; n <= entry.passThreshold; n++) {
      passPips.push(<Pip key={n} on={entry.score >= n} tone="pass" mine={n === ringAt} />);
   }

   return (
      <div className="flex shrink-0 items-center gap-1">
         {failPips}
         <i className="mx-1 h-3.5 w-px bg-gray-400" aria-hidden="true" />
         {passPips}
         <span className="ml-1.5 text-xs tabular-nums text-gray-600">
            {tallyLabel(entry, inFavour, against)}
         </span>
      </div>
   );
}

/**
 * How many people are on each side. The reader's own vote is held apart
 * from the others: an optimistic patch sets myVote before the refetch
 * carries the voter row back, so counting the rows alone would leave the
 * reader out of their own tally for a moment.
 */
function sides(
   entry: ModerationEntry,
   viewerid: number | null
): { inFavour: number; against: number } {
   const others = entry.voters.filter(voter => voter.userid !== viewerid);
   const mine = entry.myVote;

   return {
      inFavour:
         others.filter(voter => voter.score > 0).length + (mine !== null && mine > 0 ? 1 : 0),
      against: others.filter(voter => voter.score < 0).length + (mine !== null && mine < 0 ? 1 : 0),
   };
}

function VoteOutcomeBadge({ entry }: { entry: ModerationEntry }) {
   return (
      <span className="flex shrink-0 flex-wrap items-center gap-2">
         <Badge className={cn('badge-md', entry.status === 'passed' ? 'badge-green' : 'badge-red')}>
            {entry.status === 'passed' ? _js('Passed') : _js('Failed')}
         </Badge>
         <span className="flex items-center gap-1.5 text-xs text-gray-500">
            {outcomeReason(entry)}
         </span>
      </span>
   );
}

/**
 * Why it ended that way, because the word on its own misleads: a vote can
 * only fail on its own by reaching the fail threshold, and almost none do.
 *
 * "Decided by a moderator" used to stand for every settled vote carrying
 * the forced flag, which on most failed votes is nobody: the two-week
 * cron stamps each row it closes. Those say what actually happened to
 * them, and the ones a person decided, either way, name the person.
 */
function outcomeReason(entry: ModerationEntry): ReactNode {
   if (entry.decidedBy) {
      return (
         <>
            {entry.status === 'failed' ? _js('Turned down by') : _js('Carried out by')}
            <VoterChip voter={entry.decidedBy} />
         </>
      );
   }
   if (entry.score <= entry.failThreshold) {
      return _js('Voted down');
   }
   if (entry.expired) {
      return _js('Nobody voted in two weeks');
   }
   if (entry.status === 'failed') {
      return _js('Closed by another decision');
   }

   return ___p(entry.score, 'Passed with %1 vote', 'Passed with %1 votes', String(entry.score));
}

function Pip({ on, tone, mine = false }: { on: boolean; tone: 'pass' | 'fail'; mine?: boolean }) {
   const label = on ? _js('Your vote') : _js('Your vote, cancelled out');
   const pip = (
      <i
         className={cn(
            'size-2.5 rounded-sm',
            !on && (mine ? 'bg-transparent' : 'bg-gray-200'),
            on && tone === 'pass' && 'bg-green-600',
            on && tone === 'fail' && 'bg-red-600',
            mine && 'outline-2 outline-offset-1',
            mine && (tone === 'pass' ? 'outline-green-700' : 'outline-red-700')
         )}
         aria-hidden="true"
      />
   );

   if (!mine) {
      return pip;
   }

   return (
      <Tooltip>
         <TooltipTrigger asChild>
            <span className="inline-flex items-center">
               {pip}
               <span className="sr-only">{label}</span>
            </span>
         </TooltipTrigger>
         <TooltipContent>{label}</TooltipContent>
      </Tooltip>
   );
}

function tallyLabel(entry: ModerationEntry, inFavour: number, against: number): string {
   if (inFavour + against === 0) {
      return _js('No votes yet');
   }

   // A net of zero is an empty row of pips whether nobody has voted or
   // the two sides have cancelled out, so the words tell them apart.
   if (entry.score === 0) {
      return _js('%1 for, %2 against', String(inFavour), String(against));
   }

   if (entry.score < 0) {
      return _js(
         '%1 of %2 to reject',
         String(Math.abs(entry.score)),
         String(Math.abs(entry.failThreshold))
      );
   }

   return _js('%1 of %2 to pass', String(entry.score), String(entry.passThreshold));
}

/**
 * Where the voting is, on every pending card, whether or not the reader
 * can do it. The two buttons used to sit at the end of the line that says
 * when it was flagged and who has voted, worded as the thing they carry
 * out — "Make this a question" — and read as an action a moderator
 * performs rather than one vote of three. On its own strip, labelled, and
 * with the buttons saying "Vote to", the queue stops hiding its own
 * subject.
 *
 * It stays put when voting is closed to the reader, holding the reason
 * instead of the buttons: a blank where everyone else has a control says
 * nothing at all, and logged out it is the only place that offers the way
 * in.
 *
 * A settled vote has no bar. There is nothing to cast, and the outcome is
 * at the top of the card where it was.
 */
function VoteBar({
   entry,
   viewerid,
   voting,
   busy,
   onVote,
   onUndo,
   onCollapse,
}: {
   entry: ModerationEntry;
   viewerid: number | null;
   voting: VotingContext;
   busy: boolean;
   onVote: (entry: ModerationEntry, score: number) => void;
   onUndo: (entry: ModerationEntry) => void;
   /** Present on a card the reader opened back up after voting. */
   onCollapse?: () => void;
}) {
   if (entry.status !== 'pending') {
      return null;
   }

   return (
      <div className="-mx-4 flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-gray-200 bg-gray-50 px-4 py-3 text-xs">
         <span className="font-semibold uppercase tracking-wide text-gray-700">
            {_js('Your vote')}
         </span>
         <VoteState entry={entry} viewerid={viewerid} voting={voting} />
         <div className="flex shrink-0 flex-wrap gap-2 max-md:w-full max-md:justify-start md:ml-auto md:justify-end">
            <VoteActions
               entry={entry}
               voting={voting}
               busy={busy}
               onVote={onVote}
               onUndo={onUndo}
               onCollapse={onCollapse}
            />
         </div>
      </div>
   );
}

/** Where the reader stands on this vote, or why they cannot take one. */
function VoteState({
   entry,
   viewerid,
   voting,
}: {
   entry: ModerationEntry;
   viewerid: number | null;
   voting: VotingContext;
}) {
   if (entry.myVote !== null) {
      return (
         <span
            className={cn(
               'flex items-center gap-1.5 font-medium',
               entry.myVote > 0 ? 'text-green-700' : 'text-red-700'
            )}
         >
            <FaIcon name="circle-check" className="size-3.5" />
            <span>{entry.myVote > 0 ? _js('You voted in favour') : _js('You voted against')}</span>
         </span>
      );
   }

   // Whoever can vote reads the tally at the top of the card; saying it
   // again here would be the same sentence twice on one card.
   if (entry.canVote) {
      return null;
   }

   if (!voting.isLoggedIn) {
      return (
         <span className="text-gray-500">
            {_js('Anyone with %1 reputation or more can vote', String(voting.reputationThreshold))}
         </span>
      );
   }

   if (entry.subject.author?.userid === viewerid) {
      return <span className="text-gray-500">{_js('You cannot vote on your own content')}</span>;
   }

   // "Voting takes 20 reputation" read as a price: cast a vote, lose
   // twenty points. It is a doorway, and how far off it you are is the
   // part worth knowing.
   return (
      <span className="text-gray-500">
         {voting.viewerReputation === null
            ? _js('You need %1 reputation to vote', String(voting.reputationThreshold))
            : _js(
                 'You need %1 reputation to vote, and you have %2',
                 String(voting.reputationThreshold),
                 String(voting.viewerReputation)
              )}
      </span>
   );
}

/**
 * The two choices, named. A check and a cross would leave a voter to read
 * back up to the heading to learn what they were agreeing to, and the
 * queue mixes deletions with reclassifications.
 *
 * Once you have voted, the pair collapses to a withdrawal: which way you
 * went is on the strip beside it.
 */
function VoteActions({
   entry,
   voting,
   busy,
   onVote,
   onUndo,
   onCollapse,
}: {
   entry: ModerationEntry;
   voting: VotingContext;
   busy: boolean;
   onVote: (entry: ModerationEntry, score: number) => void;
   onUndo: (entry: ModerationEntry) => void;
   /** Present on a card the reader opened back up after voting. */
   onCollapse?: () => void;
}) {
   // The 44px floor below md matches the tabs and the filter; these are
   // the buttons a thumb actually works.
   const touch = 'max-md:min-h-11';

   if (entry.myVote !== null) {
      return (
         <>
            {onCollapse && (
               <button
                  type="button"
                  className={cn('button-md button-outline', touch)}
                  onClick={onCollapse}
               >
                  {_js('Hide details')}
               </button>
            )}
            <button
               type="button"
               className={cn('button-md button-outline', touch)}
               disabled={busy}
               onClick={() => onUndo(entry)}
            >
               {_js('Undo my vote')}
            </button>
         </>
      );
   }

   if (!entry.canVote) {
      return voting.isLoggedIn ? null : (
         <a href={voting.loginUrl} className={cn('button-md button-primary no-underline', touch)}>
            {_js('Log in to vote')}
         </a>
      );
   }

   return (
      <>
         <button
            type="button"
            className={cn('button-md button-outline', touch)}
            disabled={busy}
            onClick={() => onVote(entry, DOWN_VOTE)}
         >
            {entry.failText}
         </button>
         <button
            type="button"
            // Solid brand invites; a removal takes the red outline
            // instead (DESIGN.md's Escalating-Destructive Rule).
            className={cn(
               'button-md',
               touch,
               entry.destructive
                  ? 'button-outline border-red-300 text-red-600 hover:border-red-400 hover:text-red-700'
                  : 'button-primary'
            )}
            disabled={busy}
            onClick={() => onVote(entry, UP_VOTE)}
         >
            {entry.passText}
         </button>
      </>
   );
}

/**
 * The one click nothing takes back, held apart from the queue. Voting is
 * ordinarily reversible from the card, so this only appears where it isn't.
 */
function ConfirmDecision({
   entry,
   superPowers,
   onOpenChange,
   onConfirm,
}: {
   entry: ModerationEntry | null;
   superPowers: boolean;
   onOpenChange: (open: boolean) => void;
   onConfirm: (entry: ModerationEntry) => void;
}) {
   return (
      <AlertDialog open={entry !== null} onOpenChange={onOpenChange}>
         <AlertDialogContent>
            <AlertDialogHeader>
               <AlertDialogTitle>{entry?.actionText}</AlertDialogTitle>
               <AlertDialogDescription>
                  {superPowers
                     ? _js('Super powers are on, so this happens as soon as you confirm.')
                     : _js(
                          'Yours is the vote that settles this, so it happens as soon as you confirm.'
                       )}
               </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
               <AlertDialogCancel>{_js('Cancel')}</AlertDialogCancel>
               <AlertDialogAction
                  className="bg-destructive text-white hover:bg-destructive/80 focus-visible:ring-destructive/20"
                  onClick={() => entry && onConfirm(entry)}
               >
                  {entry?.actionText}
               </AlertDialogAction>
            </AlertDialogFooter>
         </AlertDialogContent>
      </AlertDialog>
   );
}

/**
 * Admins can carry out a vote on their own, which is what v1 called Super
 * Powers. It stays a deliberate switch rather than a third button: the
 * same two choices then decide the vote outright.
 *
 * Armed, it says so, because otherwise the only thing that changes on the
 * page is what the buttons do.
 */
function SuperPowers({
   enabled,
   onChange,
}: {
   enabled: boolean;
   onChange: (enabled: boolean) => void;
}) {
   return (
      <label
         className={cn(
            'flex min-h-11 cursor-pointer items-center gap-2 rounded-lg border px-3 text-sm',
            enabled
               ? 'border-red-300 bg-red-50 font-medium text-red-700'
               : 'border-transparent text-gray-700 hover:bg-gray-100'
         )}
      >
         <input
            type="checkbox"
            checked={enabled}
            onChange={event => onChange(event.target.checked)}
            className={cn(
               'size-4 cursor-pointer rounded-sm focus-visible:outline-none focus-visible:ring-[3px]',
               enabled
                  ? 'accent-red-600 focus-visible:ring-destructive/50'
                  : 'accent-brand-500 focus-visible:ring-ring/50'
            )}
         />
         <span>{enabled ? _js('Super powers on: your vote decides') : _js('Super powers')}</span>
      </label>
   );
}

/**
 * v1's keys, kept so the people who work this queue every week don't have
 * to relearn it: W and S walk the list, the arrows do the same, P and F
 * cast the vote, ? opens the list. E is gone with the toggle it opened,
 * since the flagged post is on the card now.
 */
function useQueueShortcuts({
   entries,
   enabled,
   onVote,
   onHelp,
}: {
   entries: ModerationEntry[];
   enabled: boolean;
   onVote: (entry: ModerationEntry, score: number) => void;
   onHelp: () => void;
}) {
   // The card the ring is on, by its vote rather than its place in the
   // list: voting reorders the queue, since it sorts by the date of the
   // last vote, so the card you just decided climbs to the top and a
   // remembered position would leave the ring over a post you never
   // picked. Null until W or S puts it on screen, because an outlined card
   // on arrival reads as a selection nobody made.
   const [focusedId, setFocusedId] = useState<number | null>(null);
   const engaged = focusedId !== null;
   // A queue with the keys off must not show a ring: it says the card is
   // picked while P and F do nothing.
   const focused =
      enabled && focusedId !== null ? entries.findIndex(entry => entry.voteid === focusedId) : -1;

   useEffect(() => {
      if (!enabled) {
         return;
      }

      const onKeyDown = (event: KeyboardEvent) => {
         if (isShortcutClaimed(event) || event.metaKey || event.ctrlKey || event.altKey) {
            return;
         }

         const key = event.key.toLowerCase();
         if (key === '?') {
            event.preventDefault();
            onHelp();
            return;
         }

         if (key === 'escape' && engaged) {
            setFocusedId(null);
            return;
         }

         const arrow = key === 'arrowdown' || key === 'arrowup';
         // Unasked, the arrows are how a keyboard reader scrolls; W or S
         // claims them for the queue and Escape hands them back.
         if (arrow && !engaged) {
            return;
         }

         const step =
            key === 's' || key === 'arrowdown' ? 1 : key === 'w' || key === 'arrowup' ? -1 : 0;
         if (step !== 0) {
            event.preventDefault();
            // The first press only brings the ring on screen, at the top,
            // and so does a press after the card it was on has left the
            // page.
            const next =
               focused < 0 ? 0 : Math.min(Math.max(focused + step, 0), entries.length - 1);
            setFocusedId(entries[next] ? entries[next].voteid : null);
            return;
         }

         const score = key === 'p' ? UP_VOTE : key === 'f' ? DOWN_VOTE : 0;
         const entry = entries[focused];
         if (score !== 0 && entry && entry.canVote && entry.myVote === null) {
            event.preventDefault();
            onVote(entry, score);
         }
      };

      window.addEventListener('keydown', onKeyDown);
      return () => window.removeEventListener('keydown', onKeyDown);
   }, [enabled, engaged, entries, focused, onVote, onHelp]);

   return { focused, focusOn: setFocusedId };
}

function ShortcutsDialog({
   open,
   onOpenChange,
}: {
   open: boolean;
   onOpenChange: (open: boolean) => void;
}) {
   return (
      <Dialog open={open} onOpenChange={onOpenChange}>
         <DialogContent className="max-h-[85vh] overflow-y-auto">
            <DialogTitle className="mb-2">{_js('Keyboard shortcuts')}</DialogTitle>
            <DialogDescription className="sr-only">
               {_js('A reference list of keyboard shortcuts available while moderating.')}
            </DialogDescription>
            <ShortcutList
               title={_js('The queue')}
               shortcuts={[
                  ['W', _js('Move up the queue')],
                  ['S', _js('Move down the queue')],
                  ['↑ ↓', _js('The same, once W or S has started you off')],
                  ['Esc', _js('Give the arrows back to the page')],
               ]}
            />
            <ShortcutList
               title={_js('Voting')}
               shortcuts={[
                  ['P', _js('Vote for what the flag proposes')],
                  ['F', _js('Vote against it')],
                  ['?', _js('Open this help')],
               ]}
            />
         </DialogContent>
      </Dialog>
   );
}

function voteErrorMessage(error: unknown): string {
   return error instanceof Error && error.message
      ? error.message
      : _js('That vote could not be recorded.');
}

/** Your own vote wears the other voters' chip in the tally's colors, so
 *  where you stand reads off the same line as where everyone else does. */
function Voters({ entry, viewerid }: { entry: ModerationEntry; viewerid: number | null }) {
   if (entry.voters.length === 0) {
      return <span className="italic text-gray-500">{_js('Nobody has voted yet')}</span>;
   }

   const others = entry.voters.filter(voter => voter.userid !== viewerid);
   const myself = entry.voters.find(voter => voter.userid === viewerid);

   return (
      <>
         <span className="text-gray-500">{_js('Voted')}</span>
         {entry.myVote !== null && (
            <span
               className={cn(
                  'inline-flex items-center gap-1 rounded-full py-0.5 pl-0.5 pr-2 font-semibold',
                  entry.myVote > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
               )}
            >
               <VoterAvatar voter={myself} />
               {_js('You')}
               <b>{entry.myVote > 0 ? '+' : '−'}</b>
            </span>
         )}
         {others.map(voter => (
            <VoterChip key={voter.userid} voter={voter} showScore />
         ))}
      </>
   );
}

/** A voter, small enough to sit in a line of them or beside an outcome. */
function VoterChip({ voter, showScore = false }: { voter: ModerationVoter; showScore?: boolean }) {
   return (
      <a
         href={voter.url}
         className="inline-flex items-center gap-1 rounded-full bg-gray-100 py-0.5 pl-0.5 pr-2 text-gray-700 no-underline hover:text-brand-500"
      >
         <VoterAvatar voter={voter} />
         {voter.name}
         {showScore && (
            <b className={voter.score > 0 ? 'text-green-700' : 'text-red-700'}>
               {voter.score > 0 ? '+' : '−'}
            </b>
         )}
      </a>
   );
}

function VoterAvatar({ voter }: { voter: ModerationVoter | undefined }) {
   if (!voter) {
      return null;
   }

   return (
      <img
         src={voter.avatar}
         alt=""
         className="size-4 shrink-0 rounded-full object-cover"
         loading="lazy"
      />
   );
}

/**
 * This month's five busiest voters, the one thing on the page that says
 * somebody is working this queue. A card in the queue's own weight, and
 * each cell in the leaderboard's reading order: face and name on the
 * left, the count on the right. Below md the names drop and a face and a
 * number still rank five people; above it they fit, and the ones that
 * don't are on the title attribute.
 */
function TopVoters({
   voters,
   leaderboardUrl,
}: {
   voters: ModerationTopVoter[];
   leaderboardUrl: string;
}) {
   if (voters.length === 0) {
      return null;
   }

   return (
      <section className={cn(CARD_CLASS, 'mt-4')}>
         <header className="flex items-center justify-between gap-4 border-b border-gray-200 px-4 py-2">
            <h2 className="m-0 text-sm font-semibold text-gray-900">{_js('Votes this month')}</h2>
            <a
               href={leaderboardUrl}
               className="text-sm font-medium text-brand-500 no-underline underline-offset-2 hover:text-brand-600 hover:underline"
            >
               {_js('Leaderboard')}
            </a>
         </header>
         <ul className="m-0 grid list-none grid-cols-3 divide-x divide-gray-200 p-0 md:grid-cols-5">
            {voters.map((voter, rank) => (
               <li
                  key={voter.userid}
                  className={cn('min-w-0 px-2 py-2 md:px-3', rank > 2 && 'max-md:hidden')}
               >
                  <a
                     href={voter.url}
                     title={voter.name}
                     className="flex items-center gap-2 no-underline"
                  >
                     <img
                        src={voter.avatar}
                        alt=""
                        className="size-6 shrink-0 rounded-full object-cover"
                        loading="lazy"
                     />
                     <span className="min-w-0 flex-1 truncate text-xs text-gray-500">
                        {voter.name}
                     </span>
                     <span className="ml-auto shrink-0 text-sm font-bold tabular-nums text-gray-950">
                        {voter.votes}
                     </span>
                  </a>
               </li>
            ))}
         </ul>
      </section>
   );
}

function QueueTabs({
   queues,
   activeQueue,
   viewUrl,
   onSelect,
}: {
   queues: ModerationProps['queues'];
   /** null on the permalink, which is none of the queues. */
   activeQueue: ModerationQueueKey | null;
   viewUrl: (queue: ModerationQueueKey) => string;
   /** Absent on the single-vote permalink, where a tab is a real
    *  navigation rather than a switch between lists of the same shape. */
   onSelect?: (queue: ModerationQueueKey) => void;
}) {
   return (
      <nav className="flex flex-wrap gap-1">
         {queues.map(queue => (
            <a
               key={queue.key}
               href={viewUrl(queue.key)}
               aria-current={queue.key === activeQueue ? 'page' : undefined}
               onClick={event => {
                  if (!onSelect || isModifiedClick(event)) {
                     return;
                  }
                  event.preventDefault();
                  onSelect(queue.key);
               }}
               // max-md:min-h-11 rather than a taller strip everywhere: the
               // 44px target is a thumb's, and below md is where thumbs are.
               className={cn(
                  'inline-flex items-center rounded-lg px-3 py-1.5 text-sm no-underline max-md:min-h-11',
                  'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50',
                  queue.key === activeQueue
                     ? 'bg-gray-200 font-medium text-gray-950'
                     : 'text-gray-600 hover:bg-gray-100'
               )}
            >
               {queue.label}
            </a>
         ))}
      </nav>
   );
}

/**
 * Which language's posts the queues show, defaulting to the subdomain's
 * own: fr.ifixit.com's queue was almost all English, which is backwards
 * for the people who moderate there. The menu offers the view language,
 * every language, and then whichever languages have pending posts, each
 * with its count so a moderator can see where help is needed. A post
 * whose language the model could not detect is binned as English, so no
 * choice ever hides a post from every queue.
 */
function LanguageFilter({
   langFilter,
   viewLanguage,
   otherLanguages,
   onSelect,
}: {
   langFilter: ModerationLangFilter;
   viewLanguage: ModerationProps['viewLanguage'];
   otherLanguages: ModerationProps['otherLanguages'];
   onSelect: (langFilter: ModerationLangFilter) => void;
}) {
   return (
      <label className="flex items-center gap-2 text-sm text-gray-700">
         {_js('Show')}
         {/* Live on a permalink too: a pick runs goTo, a deliberate exit
             to the queue, the same trip the tab links make. */}
         <Select value={langFilter} onValueChange={onSelect}>
            {/* The wrapping label never names a combobox: HTML-AAM
                takes aria-label, then the subtree, which is only the
                picked value. Without this it announces "English,
                combobox" and nothing says what it filters. */}
            <SelectTrigger aria-label={_js('Show language')} className="h-9 w-56 bg-white">
               <SelectValue />
            </SelectTrigger>
            <SelectContent>
               <SelectItem value={viewLanguage.key}>{viewLanguage.label}</SelectItem>
               <SelectItem value={ALL_LANGUAGES}>{_js('All languages')}</SelectItem>
               {otherLanguages.length > 0 && (
                  <>
                     <SelectSeparator />
                     <SelectGroup>
                        <SelectLabel>{_js('Languages with pending posts')}</SelectLabel>
                        {otherLanguages.map(language => (
                           /* ItemText shrink-wraps; without w-full the
                              count's ml-auto has nothing to push against. */
                           <SelectItem
                              key={language.key}
                              value={language.key}
                              className="[&>span:last-child]:w-full"
                           >
                              {language.label}
                              {language.count !== null && (
                                 /* "0 pending", never a bare "0": on Passed
                                    or Failed that language still has posts. */
                                 <span className="ml-auto text-xs tabular-nums text-gray-500">
                                    {_js('%1 pending', String(language.count))}
                                 </span>
                              )}
                           </SelectItem>
                        ))}
                     </SelectGroup>
                  </>
               )}
            </SelectContent>
         </Select>
      </label>
   );
}

function VotedFilter({
   filter,
   onSelect,
}: {
   filter: ModerationFilter;
   onSelect: (filter: ModerationFilter) => void;
}) {
   const options: Array<{ key: ModerationFilter; label: string }> = [
      { key: 'all', label: _js('All') },
      { key: 'hideMine', label: _js('Not voted by me') },
   ];

   return (
      // The gray fill belongs to the tabs above; a segmented pick is the
      // brand-tinted chip (DESIGN.md's Ring-Not-Fill Rule).
      <div role="group" aria-label={_js('Which votes to show')} className="flex flex-wrap gap-1">
         {options.map(option => (
            <button
               key={option.key}
               type="button"
               aria-pressed={option.key === filter}
               onClick={() => onSelect(option.key)}
               className={cn(
                  'rounded-lg border px-3 py-1.5 text-sm font-medium max-md:min-h-11',
                  'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50',
                  option.key === filter
                     ? 'border-brand-500 bg-brand-50 text-brand-700'
                     : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
               )}
            >
               {option.label}
            </button>
         ))}
      </div>
   );
}

function QueuePagination({
   view,
   pageData,
   pageUrl,
   onPageChange,
}: {
   view: ModerationView;
   pageData: ModerationPageData;
   pageUrl: (page: number) => string;
   onPageChange: (page: number) => void;
}) {
   const page = view.page;
   const pageCount = Math.max(1, Math.ceil(pageData.total / pageData.perPage));
   if (pageCount <= 1) {
      return null;
   }

   const range = calculatePageRange(page, pageCount, 5);
   const pages = Array.from(
      { length: range.end - range.start + 1 },
      (_unused, offset) => range.start + offset
   );
   const goTo = (event: ReactMouseEvent, target: number) => {
      if (isModifiedClick(event)) {
         return;
      }
      event.preventDefault();
      if (target >= 1 && target <= pageCount && target !== page) {
         onPageChange(target);
      }
   };

   return (
      <div className="mt-4">
         <Pagination>
            <PaginationContent className="list-none pl-0">
               <PaginationItem>
                  <PaginationPrevious
                     href={pageUrl(page - 1)}
                     aria-disabled={page === 1}
                     className={cn('no-underline', page === 1 && 'pointer-events-none opacity-50')}
                     onClick={event => goTo(event, page - 1)}
                  />
               </PaginationItem>
               {pages.map(target => (
                  <PaginationItem key={target}>
                     <PaginationLink
                        href={pageUrl(target)}
                        isActive={target === page}
                        className="no-underline"
                        onClick={event => goTo(event, target)}
                     >
                        {target}
                     </PaginationLink>
                  </PaginationItem>
               ))}
               <PaginationItem>
                  <PaginationNext
                     href={pageUrl(page + 1)}
                     aria-disabled={page === pageCount}
                     className={cn(
                        'no-underline',
                        page === pageCount && 'pointer-events-none opacity-50'
                     )}
                     onClick={event => goTo(event, page + 1)}
                  />
               </PaginationItem>
            </PaginationContent>
         </Pagination>
      </div>
   );
}

/** The card's own shape: the action heading and its tally, the question,
 *  the flagged post, and the line of voters under them. */
function QueueSkeleton({ rows = 3 }: { rows?: number }) {
   return (
      <ul aria-hidden="true" className="m-0 grid list-none gap-3 p-0">
         {Array.from({ length: Math.min(Math.max(rows, 1), 10) }, (_unused, row) => (
            <li key={row} className={cn(CARD_CLASS, 'space-y-3 p-4')}>
               <div className="flex items-center justify-between gap-4">
                  <Skeleton className="h-5 w-56" />
                  <Skeleton className="h-4 w-28" />
               </div>
               <Skeleton className="h-16 w-full rounded-xl" />
               <Skeleton className="h-24 w-full rounded-xl" />
               <Skeleton className="h-4 w-64" />
            </li>
         ))}
      </ul>
   );
}

/** behavior:'smooth' is an explicit request that the preference does not
 *  override on its own. */
function prefersReducedMotion(): boolean {
   return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
}

/** Modified clicks keep the native link behavior: new tab, new window. */
function isModifiedClick(event: ReactMouseEvent): boolean {
   return event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
}

/**
 * The queue, its page, its language and the unvoted filter all live in
 * the URL, the way they did when each was a page of its own:
 * /Moderation/Passed/3, and ?filter=hideMine or ?langFilter=de on top.
 * Switching any of them no longer reloads the page, it pushes a history
 * entry and refetches, and Back reads the view straight back out of the
 * address bar.
 */
function useQueueView(props: ModerationProps): [ModerationView, (view: ModerationView) => void] {
   const [view, setViewState] = useState<ModerationView>({
      queue: props.activeQueue,
      page: props.initialData.page,
      filter: props.initialData.filter,
      langFilter: props.initialData.langFilter,
      voteid: props.voteid,
   });

   useEffect(() => {
      const onPopState = () => setViewState(current => viewFromLocation(props, current));
      window.addEventListener('popstate', onPopState);
      return () => window.removeEventListener('popstate', onPopState);
   }, [props]);

   const setView = (next: ModerationView) => {
      // Same view, no write: clicking the queue you are already on, or the
      // filter already pressed, would otherwise bury the real previous
      // view under duplicate history entries and leave Back doing nothing.
      if (
         next.queue === view.queue &&
         next.page === view.page &&
         next.filter === view.filter &&
         next.langFilter === view.langFilter &&
         next.voteid === view.voteid
      ) {
         return;
      }

      setViewState(next);
      window.history.pushState(null, '', viewUrl(props, next));
   };

   return [view, setView];
}

function viewUrl(props: ModerationProps, view: ModerationView): string {
   const base = props.queues.find(queue => queue.key === view.queue)?.url ?? '/Moderation';
   const path = view.page > 1 ? `${base}/${view.page}` : base;

   const params = new URLSearchParams();
   if (view.filter === 'hideMine') {
      params.set('filter', 'hideMine');
   }
   // The default rides as no param at all, so the plain /Moderation link
   // keeps meaning "your language" wherever it is pasted.
   if (view.langFilter !== props.viewLanguage.key) {
      params.set(LANGUAGE_FILTER_PARAM, view.langFilter);
   }

   const query = params.toString();
   return query === '' ? path : `${path}?${query}`;
}

function parseLangFilter(props: ModerationProps, raw: string | null): ModerationLangFilter {
   const known =
      raw === ALL_LANGUAGES || props.otherLanguages.some(language => language.key === raw);

   return known && raw !== null ? raw : props.viewLanguage.key;
}

function viewFromLocation(props: ModerationProps, fallback: ModerationView): ModerationView {
   const path = window.location.pathname;
   const params = new URLSearchParams(window.location.search);

   // A permalink's trailing number is the vote, not a page of the queue;
   // only the server writes these URLs, so it is the arrival's.
   const voteMatch = path.match(/\/Vote\/(\d+)$/);
   if (voteMatch) {
      return {
         queue: props.activeQueue,
         page: props.initialData.page,
         filter: props.initialData.filter,
         langFilter: props.initialData.langFilter,
         voteid: Number(voteMatch[1]),
      };
   }

   const page = Number(path.match(/\/(\d+)$/)?.[1] ?? 1);
   const base = path.replace(/\/\d+$/, '');
   // Longest first: /Moderation is a prefix of every other queue's URL.
   const queue = [...props.queues]
      .sort((left, right) => right.url.length - left.url.length)
      .find(candidate => candidate.url === base);
   const filter = params.get('filter') === 'hideMine' ? 'hideMine' : 'all';
   const langFilter = parseLangFilter(props, params.get(LANGUAGE_FILTER_PARAM));

   return { queue: queue ? queue.key : fallback.queue, page, filter, langFilter, voteid: null };
}
